import React from 'react';
import { connect } from 'dva';
import { Layout, Form, Table, Button, Modal, Spin, Collapse, Select, Tooltip } from 'antd';
import debounce from 'lodash/debounce';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  FolderOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import AppIcon from '@/components/app-icon';
import InfoBar from '@/components/info-bar';
import SidePanel from '@/components/side-panel';
import DocDetails from './doc-details';

const columns = [
  {
    title: 'Version',
    dataIndex: 'docVersion',
    key: 'docVersion',
  },
  {
    title: 'Documentation',
    dataIndex: 'docs',
    key: 'docs',
    render: (docs) =>
      Object.entries(docs).map(([docTitle, docLink]) => (
        <Tooltip
          key={docTitle}
          title={docLink}
        >
          <a
            target="_blank"
            rel="noreferrer"
            style={{ display: 'block' }}
            href={docLink}
          >
            {docTitle}
          </a>
        </Tooltip>
      )),
  },
];

class AdminDocProduct extends React.Component {
  state = {
    modalType: '',
    activeDocType: '',
    refineParams: {},
  };

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch({ type: 'contentModel/getProductDocsAdmin' });
  }

  showModal = (modalType) => {
    this.setState({ modalType });
  };

  hideModal = () => {
    this.setState({ modalType: '' });
  };

  renderSider = () => {
    const {
      contentModel: { productDocs },
    } = this.props;

    return (
      <SidePanel type="filter">
        <Form
          layout="vertical"
          onValuesChange={debounce((_, values) => {
            this.setState({ refineParams: values });
          }, 500)}
        >
          <Form.Item
            name="docType"
            label="Type"
          >
            <Select
              allowClear
              options={Object.keys(productDocs).map((item) => ({
                label: item,
                value: item,
              }))}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(preValue, curValue) => {
              if (preValue.docType !== curValue.docType) {
                this.setState({ activeDocType: curValue.docType });

                preValue.docType &&
                  this.setFieldValue &&
                  this.setFieldValue('docVersion', undefined);
                return true;
              }
              return false;
            }}
          >
            {({ getFieldValue, setFieldValue }) => {
              const docType = getFieldValue('docType');
              this.setFieldValue = setFieldValue;

              if (!docType) return null;
              return (
                <Form.Item
                  name="docVersion"
                  label="Version"
                >
                  <Select
                    allowClear
                    options={Object.keys(productDocs[docType]).map((item) => ({
                      label: item,
                      value: item,
                    }))}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </SidePanel>
    );
  };

  refineProductDocs = () => {
    const {
      contentModel: { productDocs },
    } = this.props;
    const { refineParams } = this.state;

    if (!refineParams || !refineParams.docType) return productDocs;

    let result = { [refineParams.docType]: productDocs[refineParams.docType] };

    if (refineParams.docVersion) {
      result[refineParams.docType] = {
        [refineParams.docVersion]: productDocs[refineParams.docType][refineParams.docVersion],
      };
    }

    return result;
  };

  render() {
    const { contentModel, dispatch } = this.props;
    const { modalType, activeDocType } = this.state;
    const productDocs = this.refineProductDocs();

    return (
      <div>
        <InfoBar
          icon="MCP_app_menu_Network_audits"
          color="#0271bc"
          title="Product Documentation"
          subtitle="Manage the Product Documentation"
          actions={[
            {
              label: 'New',
              icon: <AppIcon name="add" />,
              onClick: () => this.showModal('new'),
            },
          ]}
        />
        <Layout>
          {this.renderSider()}
          <Layout.Content className="doc-container">
            <Spin
              style={{ height: 400 }}
              spinning={!!contentModel.loading}
            >
              <Collapse
                accordion
                ghost
                bordered={false}
                className="folder-collapse"
                activeKey={activeDocType}
                onChange={(activeDocType) => this.setState({ activeDocType })}
                expandIcon={({ isActive }) => (
                  <span>
                    {isActive ? <CaretDownOutlined /> : <CaretRightOutlined />}
                    {isActive ? <FolderOpenOutlined /> : <FolderOutlined />}
                  </span>
                )}
              >
                {Object.entries(productDocs).map(([docType, docVersions]) => (
                  <Collapse.Panel
                    key={docType}
                    header={docType}
                  >
                    <Table
                      bordered
                      size="small"
                      pagination={false}
                      rowKey="docVersion"
                      columns={[
                        ...columns,
                        {
                          title: 'Action',
                          dataIndex: 'docVersion',
                          key: 'action',
                          render: (docVersion) => (
                            <Button
                              type="primary"
                              onClick={() => {
                                Modal.confirm({
                                  title: 'Warning',
                                  content: 'This will remove the product documentation.',
                                  cancelButtonProps: { type: 'text' },
                                  onOk: () => {
                                    this.props.dispatch({
                                      type: 'contentModel/deleteProductDocs',
                                      payload: { docVersion, docType },
                                    });
                                  },
                                });
                              }}
                            >
                              Delete
                            </Button>
                          ),
                        },
                      ]}
                      dataSource={Object.entries(docVersions).map(([docVersion, docs]) => ({
                        docVersion,
                        docs,
                      }))}
                    />
                  </Collapse.Panel>
                ))}
              </Collapse>
            </Spin>
          </Layout.Content>
        </Layout>
        {modalType === 'new' && (
          <DocDetails
            docTypes={Object.keys(contentModel.productDocs)}
            onCancel={this.hideModal}
            dispatch={dispatch}
          />
        )}
      </div>
    );
  }
}

export default connect(({ contentModel }) => ({
  contentModel,
}))(AdminDocProduct);
